import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { AppBar, Box, CircularProgress, Stack, Tab, Tabs } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { FilterProvider } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { useHasBillingContracts } from "@/partner/entities/billing";
import { BillingInvoicesFilter } from "./BillingInvoicesFilter";
import { BillingInvoices } from "./BillingInvoices";
import { BillingReports } from "./BillingReports";

type BillingTab = "invoices" | "reports";

function parseBillingTab(value: string | null): BillingTab {
  return value === "reports" ? "reports" : "invoices";
}

// Раздел скрыт целиком у организации без контрактов (спека §10): пункт
// меню прячет filterMenuItems (widgets/layout), а по прямому адресу здесь
// вместо вкладок с фильтром и пустыми таблицами показан один понятный экран.
export const Billing: FC = () => {
  const { t } = useTranslation();
  const { hasContracts, loading } = useHasBillingContracts();

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasContracts) {
    return <EmptyListCard title={t("billing.invoices.empty")} />;
  }

  return (
    <FilterProvider>
      <BillingContent />
    </FilterProvider>
  );
};

// Один пункт меню и одна страница с переключателем вкладок (?tab=
// invoices|reports), как у Возвратов/Диспутов (features/chargebacks). У
// счетов и отчётов разные фильтры (у отчётов фильтра нет вовсе), поэтому,
// в отличие от chargebacks, карточка фильтра общая не одна на обе вкладки:
// `BillingInvoicesFilter` показывается только на вкладке счетов.
const BillingContent: FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = parseBillingTab(searchParams.get("tab"));

  const items: { name: BillingTab; label: string }[] = [
    { name: "invoices", label: t("billing.tabs.invoices") },
    { name: "reports", label: t("billing.tabs.reports") },
  ];

  const onSelectTab = (name: BillingTab) => {
    if (name === tab) return;
    const next = new URLSearchParams(searchParams);
    next.set("tab", name);
    // Фильтр (статус, даты) относится только к счетам, на отчётах смысла
    // не имеет; страница у выборки тоже своя для каждой вкладки.
    next.delete("filter[status]");
    next.delete("filter[dateFrom]");
    next.delete("filter[dateTo]");
    next.delete("page");
    setSearchParams(next);
  };

  return (
    <Stack spacing={1}>
      {tab === "invoices" && <BillingInvoicesFilter />}
      <AppBar position="static" color="default" elevation={0}>
        <Tabs value={tab} scrollButtons="auto" variant="scrollable">
          {items.map(({ name, label }) => (
            <Tab
              key={name}
              value={name}
              label={label}
              onClick={() => onSelectTab(name)}
            />
          ))}
        </Tabs>
      </AppBar>
      {tab === "invoices" ? <BillingInvoices /> : <BillingReports />}
    </Stack>
  );
};
